// Lightweight console ring buffer for crash diagnostics.
// Installed at app boot; captures the last N console entries.

export type ConsoleEntry = {
  level: "log" | "info" | "warn" | "error" | "debug";
  ts: number;
  message: string;
};

const MAX_ENTRIES = 20;
const buffer: ConsoleEntry[] = [];
let installed = false;

function serializeArg(a: unknown): string {
  try {
    if (a instanceof Error) return `${a.name}: ${a.message}`;
    if (typeof a === "string") return a;
    return JSON.stringify(a, (_k, v) => (v instanceof Error ? `${v.name}: ${v.message}` : v));
  } catch {
    return String(a);
  }
}

function push(level: ConsoleEntry["level"], args: unknown[]) {
  const message = args.map(serializeArg).join(" ").slice(0, 2000);
  buffer.push({ level, ts: Date.now(), message });
  if (buffer.length > MAX_ENTRIES) buffer.shift();
}

export function installConsoleBuffer() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  const levels: ConsoleEntry["level"][] = ["log", "info", "warn", "error", "debug"];
  for (const lvl of levels) {
    const original = (console as unknown as Record<string, (...a: unknown[]) => void>)[lvl];
    if (typeof original !== "function") continue;
    (console as unknown as Record<string, (...a: unknown[]) => void>)[lvl] = (...args: unknown[]) => {
      try { push(lvl, args); } catch { /* noop */ }
      original.apply(console, args);
    };
  }
}

export function getConsoleBuffer(): ConsoleEntry[] {
  return buffer.slice();
}
